import { FinancialAssetDto, LifeInsuranceDto, RealEstateDto, VehicleDto } from "@/lib/DTMWebAPIClient.generated";
import { RootState } from "@/store/dtm";
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export interface assetInfo {
    displayId: string,
    id: number,
    visible: boolean,
    assetCategory: string,
    assetType: string,
    origin: string,
    value: number,
    valueText: string,
    description: string,
    downPaymentValue: number,
    downPaymentValueText: string,
    applicants: number[],
    details: assetDetails
}

export interface AssetOwnership {
    applicantId: number,
    fullname: string,
}

export interface assetDetails {
    nonRegistered: FinancialAsset | undefined,
    registered: FinancialAsset | undefined,
    realEstate: RealEstateAsset | undefined,
    vehicle: VehicleAsset | undefined,
    lifeInsurance: LifeInsuranceAsset | undefined
}

export interface FinancialAsset {
    financialInstitution: string,
    otherFinancialInstitution: string,
}

export interface VehicleAsset {
    ownership: string,
    year: string,
    make: string,
    model: string,
}

export interface VehicleOption {
    code: string | undefined;
    name: string | undefined;
}

export interface RealEstateAsset {
    occupancyType: string,
    propertyType: string | undefined,
    rentalComponent: string,
    numOfProperties: string,
    ownershipPercentage: string,
    address: {
        displayId: string,
        streetNumber: string,
        streetName: string,
        streetType: string,
        streetDirection: string,
        unitType: string,
        unitNumber: string,
        city: string,
        province: string,
        postalCode: string,
        country: string,
    },
    originalPurchasePrice: number,
    originalPurchasePriceText: string,
    originalPurchaseDate: string,
    propertyTax: number,
    propertyTaxText: string,
    heatingCost: number,
    heatingCostText: string,
    maintenanceFee: number,
    maintenanceFeeText: string,
    lot: string,
    block: string,
    plan: string,
    section: string,
    futureUsage: string,
    hasPropertyBeenSold: string,
    soldDate: string,
    rentalIncome: number,
    rentalIncomeText: string,
    manualAddressEntered: boolean,
    isCurrentResidence: boolean,
}

export interface LifeInsuranceAsset {
    cashSurrenderValue: number,
    cashSurrenderValueText: string,
}

export interface assetState {
    assets: assetInfo[],
}

const initialState: assetState = {
    assets: [],
}


export const assetSlice = createSlice({
    name: "asset",
    initialState,
    reducers: {
        addAsset: (state, action: PayloadAction<assetInfo>) => {
            const asset = action.payload;
            state.assets.push(asset);
        },
        setAssetInfo: (state, action: PayloadAction<assetInfo>) => {
            const assetInfo = action.payload;
            const idx = state.assets.findIndex(asset => asset.displayId === assetInfo.displayId);
            if (idx !== -1) {
                state.assets[idx] = assetInfo;
            }
        },
        toggleAssetDisplay: (state, action: PayloadAction<number>) => {
            const id = action.payload;
            state.assets[id].visible = !state.assets[id].visible;
        },
        removeAsset: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            state.assets = state.assets.filter(asset => asset.displayId != id);

            //Reset the displayId of remaining assets
            state.assets = state.assets.map((asset, index) => ({
                ...asset,
                displayId: `${index + 1}`
            }));
        },
        clearAssetState: (state) => {
            state.assets = [];
        }
    }
});

export const { addAsset, setAssetInfo, toggleAssetDisplay, removeAsset, clearAssetState } = assetSlice.actions

export default assetSlice.reducer

export const selectAsset = (state: RootState) => state.assets;